import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { SunLogo } from "./SunLogo";
import { BUSINESS, SERVICES, CITIES } from "../data/site";

const NavLink = ({ to, children, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="text-sm font-medium text-gray-300 hover:text-[#FF9100] transition-colors duration-200"
  >
    {children}
  </Link>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [group, setGroup] = useState(null);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 bg-[#0D0E11]/90 backdrop-blur-xl border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 group">
            <SunLogo size={38} />
            <span className="font-heading leading-tight">
              <span className="block font-bold text-white text-lg group-hover:text-[#FF9100] transition-colors duration-200">1st Nations Security</span>
              <span className="block text-[10px] font-semibold uppercase tracking-widest text-[#FF9100]">Manitoba Security Services</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7" aria-label="Main navigation">
            <NavLink to="/" testid="nav-home">Home</NavLink>
            <div className="relative group">
              <button
                data-testid="nav-services-toggle"
                className="flex items-center gap-1 text-sm font-medium text-gray-300 hover:text-[#FF9100] transition-colors duration-200"
              >
                Services <ChevronDown className="w-4 h-4" />
              </button>
              <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-200 absolute left-0 top-full pt-2 w-72">
                <div className="bg-[#14161C] border border-white/10 rounded-xl shadow-2xl p-2 max-h-[70vh] overflow-y-auto">
                  <Link to="/security-services" data-testid="nav-services-all" className="block px-3 py-2 text-sm font-semibold text-[#FF9100] hover:bg-white/5 rounded-lg">All Security Services</Link>
                  {SERVICES.map((s) => (
                    <Link key={s.slug} to={`/security-services/${s.slug}`} data-testid={`nav-service-${s.slug}`} className="block px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-lg">
                      {s.title}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            <div className="relative group">
              <button
                data-testid="nav-areas-toggle"
                className="flex items-center gap-1 text-sm font-medium text-gray-300 hover:text-[#FF9100] transition-colors duration-200"
              >
                Service Areas <ChevronDown className="w-4 h-4" />
              </button>
              <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-200 absolute left-0 top-full pt-2 w-[30rem]">
                <div className="bg-[#14161C] border border-white/10 rounded-xl shadow-2xl p-3 max-h-[70vh] overflow-y-auto">
                  <Link to="/service-areas" data-testid="nav-areas-all" className="block px-3 py-2 text-sm font-semibold text-[#FF9100] hover:bg-white/5 rounded-lg">All Manitoba Service Areas</Link>
                  <div className="grid grid-cols-2 gap-x-2">
                    {CITIES.map((c) => (
                      <Link key={c.slug} to={`/security-guards/${c.slug}`} data-testid={`nav-city-${c.slug}`} className="block px-3 py-1.5 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-lg">
                        {c.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <NavLink to="/indigenous-security" testid="nav-indigenous">Indigenous Security</NavLink>
            <NavLink to="/about" testid="nav-about">About</NavLink>
            <NavLink to="/contact" testid="nav-contact">Contact</NavLink>
            <Link to="/francais" data-testid="nav-fr" className="text-xs font-bold text-[#FF9100] border border-[#FF9100]/40 rounded-full px-3 py-1.5 hover:bg-[#FF9100]/10 transition-colors">FR</Link>
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={BUSINESS.phoneHref}
              data-testid="header-call-btn"
              className="hidden md:inline-flex items-center gap-2 bg-[#FF9100] hover:bg-[#FF5500] text-[#0D0E11] font-semibold text-sm px-4 py-2 rounded-full transition-colors duration-200"
            >
              <Phone className="w-4 h-4" /> {BUSINESS.phone}
            </a>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
              className="lg:hidden text-gray-200 hover:text-[#FF9100] transition-colors"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-white/10 bg-[#0D0E11] max-h-[75vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <Link to="/" onClick={() => setOpen(false)} data-testid="mobile-nav-home" className="block py-2 text-gray-200 font-medium">Home</Link>
            <button
              data-testid="mobile-services-toggle"
              onClick={() => setGroup(group === "services" ? null : "services")}
              className="flex w-full items-center justify-between py-2 text-gray-200 font-medium"
            >
              Services <ChevronDown className={`w-4 h-4 transition-transform ${group === "services" ? "rotate-180" : ""}`} />
            </button>
            {group === "services" && (
              <div className="pl-4 space-y-1">
                <Link to="/security-services" onClick={() => setOpen(false)} data-testid="mobile-nav-services-all" className="block py-1.5 text-[#FF9100] text-sm font-semibold">All Services</Link>
                {SERVICES.map((s) => (
                  <Link key={s.slug} to={`/security-services/${s.slug}`} onClick={() => setOpen(false)} data-testid={`mobile-service-${s.slug}`} className="block py-1.5 text-sm text-gray-400">{s.title}</Link>
                ))}
              </div>
            )}
            <button
              data-testid="mobile-areas-toggle"
              onClick={() => setGroup(group === "areas" ? null : "areas")}
              className="flex w-full items-center justify-between py-2 text-gray-200 font-medium"
            >
              Service Areas <ChevronDown className={`w-4 h-4 transition-transform ${group === "areas" ? "rotate-180" : ""}`} />
            </button>
            {group === "areas" && (
              <div className="pl-4 space-y-1">
                <Link to="/service-areas" onClick={() => setOpen(false)} data-testid="mobile-nav-areas-all" className="block py-1.5 text-[#FF9100] text-sm font-semibold">All Service Areas</Link>
                {CITIES.map((c) => (
                  <Link key={c.slug} to={`/security-guards/${c.slug}`} onClick={() => setOpen(false)} data-testid={`mobile-city-${c.slug}`} className="block py-1.5 text-sm text-gray-400">{c.name}</Link>
                ))}
              </div>
            )}
            <Link to="/indigenous-security" onClick={() => setOpen(false)} data-testid="mobile-nav-indigenous" className="block py-2 text-gray-200 font-medium">Indigenous Security</Link>
            <Link to="/about" onClick={() => setOpen(false)} data-testid="mobile-nav-about" className="block py-2 text-gray-200 font-medium">About</Link>
            <Link to="/contact" onClick={() => setOpen(false)} data-testid="mobile-nav-contact" className="block py-2 text-gray-200 font-medium">Contact</Link>
            <Link to="/francais" onClick={() => setOpen(false)} data-testid="mobile-nav-fr" className="block py-2 text-[#FF9100] font-bold">Français</Link>
            <a href={BUSINESS.phoneHref} data-testid="mobile-call-btn" className="mt-2 flex items-center justify-center gap-2 bg-[#FF9100] text-[#0D0E11] font-semibold px-4 py-3 rounded-full">
              <Phone className="w-4 h-4" /> {BUSINESS.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
